import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { Popper } from '@mui/material';
import { BaseImage, Tooltip, Variables } from '../base';
import {
    entityBadgeBaseStyles,
    entityBadgeHoverStyles,
    entityBadgeTokens,
    BADGE_ICON_SIZE,
    entityBadgeActionButtonHoverStyles,
    metricBadgeBaseStyles,
    metricBadgeHoverStyles,
    metricBadgeTokens,
    metricBadgeActionButtonHoverStyles,
    metricBadgeLabeledValueHoverStyles,
} from './entity-badge.styles';

export const ActionMainItem = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    height: 100%;
    padding: 0 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const ActionLabel = styled.div`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

export const ActionSymbol = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: inherit;

    svg {
        width: ${BADGE_ICON_SIZE};
        height: ${BADGE_ICON_SIZE};
        fill: currentColor;
    }
`;

export const ActionIcon = styled(Icon)`
    width: ${BADGE_ICON_SIZE};
    height: ${BADGE_ICON_SIZE};
    fill: currentColor;
`;

export const ActionTooltip = styled(Tooltip)`
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    cursor: pointer;
    padding: 0;

    & ${ActionLabel} {
        flex-grow: 1;
        min-width: 0;
    }
`;

export const ActionButton = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    border-left: 1px solid ${entityBadgeTokens.border};
    padding: 0 8px;

    & > ${ActionIcon} {
        fill: currentColor;
    }
`;

export const MetricActionButton = styled(ActionButton)`
    border-left-color: ${metricBadgeTokens.border};
`;

export const ActionMenuDivider = styled.div`
    height: 1px;
    border-bottom: 1px solid ${entityBadgeTokens.border};
`;

export const ActionMenuItem = styled.button`
    outline: none;
    user-select: none;
    background-color: unset;
    text-align: left;
    border: none;
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding: 0;
    font: ${entityBadgeTokens.font};
    color: ${entityBadgeTokens.color};
    background-color: ${entityBadgeTokens.background};

    & > ${ActionTooltip}, & > ${ActionLabel} {
        padding: 6px 4px;
    }

    &:hover {
        cursor: pointer;
        background-color: ${Variables.Colors.b300_l90};
    }

    &:disabled {
        color: ${Variables.Colors.n50};
        background-color: ${Variables.Colors.n15_48};
        cursor: default;
    }
`;

export const Badge = styled.div`
    ${entityBadgeBaseStyles}
    padding: 0;
    overflow: hidden;
    justify-content: space-between;

    ${entityBadgeHoverStyles}

    &:hover {
        ${ActionButton} {
            ${entityBadgeActionButtonHoverStyles}
        }
    }
`;

export const MetricBadge = styled.div`
    ${metricBadgeBaseStyles}
    padding: 0;
    overflow: hidden;
    justify-content: space-between;

    ${metricBadgeHoverStyles}
    ${metricBadgeLabeledValueHoverStyles}

    &:hover {
        ${MetricActionButton} {
            ${metricBadgeActionButtonHoverStyles}
        }
    }
`;

export const WrappedPopper = styled(Popper)`
    position: absolute;
    z-index: 10002;
    ${({ anchorEl }) => {
        if (!anchorEl) {
            return;
        }
        const el = anchorEl as HTMLDivElement;
        const rect = el.getBoundingClientRect();

        const aw = rect.width;
        return `width: ${aw}px;`;
    }}
    margin-top: 1px;

    border: 1px solid ${entityBadgeTokens.border};
    border-radius: 8px;
    box-shadow: ${Variables.Shadows.medium};
`;

export const ImageSymbol = styled(BaseImage)`
    height: 16px;
    width: 16px;
`;
